import { useEffect, useState } from 'react';

/**
 * Browser connectivity, tracked via `navigator.onLine` plus the `online` /
 * `offline` window events. Coarse by design — it reflects link-layer
 * connectivity, not whether Firestore is actually reachable — but that's the
 * right signal for warning a caregiver that edits won't save while their
 * device is offline. We use an in-memory Firestore cache (see lib/firebase.ts),
 * so an edit made offline pends and is lost on reload rather than being
 * durably queued.
 */
export function useOnlineStatus(): boolean {
  const [online, setOnline] = useState(() =>
    typeof navigator === 'undefined' ? true : navigator.onLine,
  );

  useEffect(() => {
    const goOnline = () => setOnline(true);
    const goOffline = () => setOnline(false);
    window.addEventListener('online', goOnline);
    window.addEventListener('offline', goOffline);
    // Resync in case connectivity flipped between first render and this effect.
    setOnline(navigator.onLine);
    return () => {
      window.removeEventListener('online', goOnline);
      window.removeEventListener('offline', goOffline);
    };
  }, []);

  return online;
}
